/**
 * Vue wiring for product settings.
 *
 * Resolves who is asking from the store and the app config, delegates the shape
 * and merge rules to `productSettings`, and commits the result. Components get
 * `save({ oneKey: value })` and nothing else: no payload building, no API call,
 * no store write of their own.
 *
 * The rules module stays free of Vue and Pinia so it can be tested as plain
 * functions; this file is the only part that needs a component context.
 */
import { computed } from 'vue'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'

import { normalizeProductSettings, readLanguageSettings, saveProductSettings } from './productSettings'
import type { ProductSettingsPatch } from './productSettings'
import { useAccountSettingsStore } from './stores/accountSettings'

export const useProductSettings = () => {
  const accountSettings = useAccountSettingsStore()
  const appConfig = useJazelAppConfig()

  /** Current settings, flattened, whichever shape the store happens to hold. */
  const settings = computed(() => normalizeProductSettings(accountSettings.productSettings))

  /** The stored language choices, with `same-as-description` preserved. */
  const languages = computed(() => readLanguageSettings(accountSettings.productSettings))

  /**
   * Persist a change to one or more settings. Resolves true when saved. The
   * store is updated only on success, so a rejected write leaves the UI
   * showing what the server still holds.
   */
  const save = async (patch: ProductSettingsPatch): Promise<boolean> => {
    const { saved, settings: merged } = await saveProductSettings(
      {
        accountId: accountSettings.accountId,
        productId: String((appConfig as { productId?: unknown })?.productId ?? ''),
        isClientUser: accountSettings.isClientUser,
        sessionToken: accountSettings.sessionToken,
        current: accountSettings.productSettings,
      },
      patch,
    )

    if (saved) accountSettings.setProductSettings(merged as IProductSettings)

    return saved
  }

  return { settings, languages, save }
}
